import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';

function getAllItemIds(item: ClassificationItem): number[] {
  return [item.id, ...(item.children?.flatMap(getAllItemIds) ?? [])];
}

export const classificationAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getAllElementGroups: builder.query({
      query: (organisationId) =>
        `classification-trees/organisation/${organisationId}`,
      transformResponse: (response: { data: any[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ElementGroups' as const,
                id,
              })),
              { type: 'ElementGroups', id: 'LIST' },
            ]
          : [{ type: 'ElementGroups', id: 'LIST' }],
    }),

    getElementGroup: builder.query({
      query: (elementGroupId) => `classification-trees/${elementGroupId}`,
      transformResponse: (response: { data: any }) => response.data,
      providesTags: (result) =>
        result ? [{ type: 'ElementGroups' as const, id: result.id }] : [],
    }),

    getElementGroups: builder.query<Record<number, any>, number[]>({
      queryFn: async (elementGroupIds, _queryApi, _extraOptions, baseQuery) => {
        try {
          const results = await Promise.all(
            elementGroupIds.map((groupId) =>
              baseQuery(`classification-trees/${groupId}`)
            )
          );

          // Check for errors
          const errors = results.filter((result) => result.error);
          if (errors.length > 0) {
            return { error: { status: 'CUSTOM_ERROR', data: errors } };
          }

          // Transform responses into a mapped object
          const transformedData = results.reduce((acc, result, index) => {
            if (result.data) {
              //@ts-expect-error - elementGroupIds is an array of numbers
              acc[elementGroupIds[index]] = result.data.data;
            }
            return acc;
          }, {} as Record<number, any>);

          return { data: transformedData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) => [
        'ElementGroups',
        ...(result
          ? Object.keys(result).map((id) => ({
              type: 'ElementGroups' as const,
              id: Number(id),
            }))
          : []),
      ],
    }),

    createElementGroup: builder.mutation({
      query: (elementGroup) => ({
        url: 'classification-trees',
        method: 'POST',
        body: elementGroup,
      }),
      invalidatesTags: ['ElementGroups'],
    }),

    updateElementGroup: builder.mutation({
      query: ({ id, ...elementGroup }) => ({
        url: `classification-trees/${id}`,
        method: 'PUT',
        body: elementGroup,
      }),
      invalidatesTags: ['ElementGroups'],
    }),

    getElementTree: builder.query<ClassificationItem[], number>({
      query: (treeId) => `classification-trees/${treeId}/items`,
      transformResponse: (response: ClassificationItem[]) =>
        transformClassificationTree.buildTree(response),
      providesTags: (result, error, treeId) => [
        { type: 'ElementTree', id: treeId },
        ...(result
          ?.flatMap((item) => getAllItemIds(item))
          .map((id) => ({ type: 'ElementItem' as const, id })) ?? []),
      ],
    }),

    getElementTrees: builder.query<
      Record<number, ClassificationItem[]>,
      number[]
    >({
      queryFn: async (
        treeIds: number[],
        _queryApi,
        _extraOptions,
        baseQuery
      ) => {
        try {
          // Fetch all trees in parallel
          const results = await Promise.all(
            treeIds.map((treeId) =>
              baseQuery(`classification-trees/${treeId}/items`)
            )
          );

          // Check for any errors
          const errors = results.filter((result) => result.error);
          if (errors.length > 0) {
            return { error: { status: 'CUSTOM_ERROR', data: errors } };
          }

          // Transform the results into a mapped object
          const transformedData = results.reduce((acc, result, index) => {
            if (result.data) {
              acc[treeIds[index]] = transformClassificationTree.buildTree(
                result.data as ClassificationItem[]
              );
            }
            return acc;
          }, {} as Record<number, ClassificationItem[]>);

          return { data: transformedData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result, error, treeIds) => [
        // Provide tags for each tree
        ...treeIds.map((treeId) => ({
          type: 'ElementTree' as const,
          id: treeId,
        })),
        // Provide tags for all items in all trees
        ...(result
          ? Object.values(result).flatMap((tree) =>
              tree
                .flatMap((item) => getAllItemIds(item))
                .map((id) => ({ type: 'ElementItem' as const, id }))
            )
          : []),
      ],
    }),

    archiveElementTreeForUser: builder.mutation({
      query: (classificationTreeId) => ({
        url: `classification-trees/${classificationTreeId}/status`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ElementTree', id },
        { type: 'ElementGroups', id },
        { type: 'ElementGroups', id: 'LIST' },
      ],
    }),

    archiveElementTree: builder.mutation({
      query: (classificationTreeId) => ({
        url: `classification-trees/${classificationTreeId}/archive`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ElementTree', id },
        { type: 'ElementGroups', id },
        { type: 'ElementGroups', id: 'LIST' },
      ],
    }),

    duplicateClassificationTree: builder.mutation({
      query: (classificationTreeId) => ({
        url: `classification-trees/${classificationTreeId}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ElementGroups', id: 'LIST' }],
    }),

    createClassificationItem: builder.mutation({
      query: ({ classification_tree_id, ...item }) => ({
        url: `classification-trees/${classification_tree_id}/items`,
        method: 'POST',
        body: item,
      }),
      invalidatesTags: (result, error, { classification_tree_id }) => [
        { type: 'ElementTree', id: classification_tree_id },
        { type: 'ElementGroups', id: classification_tree_id },
      ],
    }),

    updateClassificationItem: builder.mutation({
      query: ({ classificationItem, ...item }) => ({
        url: `classification-items/${classificationItem}`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: (result, error, { classificationItem }) => [
        { type: 'ElementItem', id: classificationItem },
      ],
    }),

    addObservation: builder.mutation({
      query: ({ classificationItem, observation }) => ({
        url: `classification-trees/element/${classificationItem}/observations`,
        method: 'POST',
        body: observation,
      }),
      invalidatesTags: ['Observations'],
    }),

    getObservations: builder.query({
      query: ({ classificationItem, page = 1, perPage = 10 }) => ({
        url: `classification-trees/element/${classificationItem}/observations`,
        params: { page, per_page: perPage },
      }),
      providesTags: ['Observations'],
    }),

    updateObservation: builder.mutation({
      query: ({ classificationItem, comment, observation }) => ({
        url: `classification-trees/element/${classificationItem}/observations/${comment}`,
        method: 'PUT',
        body: observation,
      }),
      invalidatesTags: ['Observations'],
    }),

    deleteObservation: builder.mutation({
      query: ({ classificationItem, comment }) => ({
        url: `classification-trees/element/${classificationItem}/observations/${comment}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Observations'],
    }),

    addRecommendation: builder.mutation({
      query: ({ classificationItem, recommendation }) => ({
        url: `classification-trees/element/${classificationItem}/recommendations`,
        method: 'POST',
        body: recommendation,
      }),
      invalidatesTags: ['Recommendations'],
    }),

    getRecommendations: builder.query({
      query: ({ classificationItem, page = 1, perPage = 10 }) => ({
        url: `classification-trees/element/${classificationItem}/recommendations`,
        params: { page, per_page: perPage },
      }),
      providesTags: ['Recommendations'],
    }),

    updateRecommendation: builder.mutation({
      query: ({ classificationItem, recommendationId, recommendation }) => ({
        url: `classification-trees/element/${classificationItem}/recommendations/${recommendationId}`,
        method: 'PUT',
        body: recommendation,
      }),
      invalidatesTags: ['Recommendations'],
    }),

    deleteRecommendation: builder.mutation({
      query: ({ classificationItem, recommendationId }) => ({
        url: `classification-trees/element/${classificationItem}/recommendations/${recommendationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Recommendations'],
    }),

    handleObservations: builder.mutation({
      query: ({ classificationItem, ...rest }) => ({
        url: `classification-trees/element/${classificationItem}/observations`,
        method: 'POST',
        body: { observations: rest.observations },
      }),
      invalidatesTags: ['Observations'],
    }),

    handleRecommendations: builder.mutation({
      query: ({ classificationItem, ...rest }) => ({
        url: `classification-trees/element/${classificationItem}/recommendations`,
        method: 'POST',
        body: { recommendations: rest.recommendations },
      }),
      invalidatesTags: ['Recommendations'],
    }),
  }),
});

export const {
  useGetAllElementGroupsQuery,
  useGetElementGroupQuery,
  useGetElementGroupsQuery,
  useCreateElementGroupMutation,
  useUpdateElementGroupMutation,
  useArchiveElementTreeForUserMutation,
  useArchiveElementTreeMutation,
  useDuplicateClassificationTreeMutation,
  useGetElementTreeQuery,
  useGetElementTreesQuery,
  useCreateClassificationItemMutation,
  useUpdateClassificationItemMutation,
  useAddObservationMutation,
  useGetObservationsQuery,
  useUpdateObservationMutation,
  useDeleteObservationMutation,
  useAddRecommendationMutation,
  useGetRecommendationsQuery,
  useUpdateRecommendationMutation,
  useDeleteRecommendationMutation,
  useHandleObservationsMutation,
  useHandleRecommendationsMutation,
} = classificationAPI;
